<template>
  <div class="page-outer hall-products-page">
    <div class="page-header page-header-hall-products">
      <div class="header-left header-left--inline">
        <h1 class="page-title">
          <i class="ri-list-check"></i>
          編輯館別商品<span v-if="hallName">：{{ hallName }}</span>
        </h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" :disabled="saving || loadingMain || !!loadError" @click="goBack">返回列表</button>
        <button type="button" class="btn btn-primary" :disabled="saving || loadingMain || !!loadError" @click="saveHallProducts">
          <span v-if="saving" class="spinner-sm"></span>
          {{ saving ? '儲存中...' : '儲存商品' }}
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loadingMain" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="loadError" class="error-state">
        <i class="ri-error-warning-line"></i> {{ loadError }}
        <button type="button" class="btn btn-secondary btn-sm" @click="loadAll">重新載入</button>
      </div>

      <div v-else class="form-container">
        <p class="form-hint intro-hint">排序依下方清單由上到下；可拖拉、點上下移，或直接輸入排序數字調整。多規格商品請在挑選視窗按一次「加入」即可（以整個商品為單位）。</p>
        <div v-if="!hallProducts.length" class="empty-hint">尚無商品，點選下方「新增商品」。</div>
        <div v-else class="hall-products-list">
          <div
            v-for="(p, idx) in hallProducts"
            :key="p.product_id"
            class="hall-product-row"
            draggable="true"
            :class="{ 'dragging': dragIndex === idx, 'drag-over': dragOverIndex === idx && dragIndex !== idx }"
            @dragstart="onDragStart(idx, $event)"
            @dragover.prevent="onDragOver(idx)"
            @dragleave="onDragLeave(idx)"
            @drop.prevent="onDrop(idx)"
            @dragend="onDragEnd"
          >
            <span class="drag-handle" title="拖曳排序">
              <i class="ri-drag-move-2-line"></i>
            </span>
            <img v-if="hallProductImageUrl(p)" :src="hallProductImageUrl(p)" class="hall-product-thumb" alt="" />
            <div v-else class="hall-product-thumb-placeholder"><i class="ri-image-line"></i></div>
            <div class="hall-product-name">{{ p.product_name }}</div>
            <div class="sort-controls">
              <button
                type="button"
                class="btn-icon"
                title="上移"
                :disabled="idx === 0"
                @click.stop="moveUp(idx)"
              >
                <i class="ri-arrow-up-s-line"></i>
              </button>
              <button
                type="button"
                class="btn-icon"
                title="下移"
                :disabled="idx === hallProducts.length - 1"
                @click.stop="moveDown(idx)"
              >
                <i class="ri-arrow-down-s-line"></i>
              </button>
              <input
                type="number"
                class="sort-input"
                min="1"
                :max="hallProducts.length"
                :value="idx + 1"
                title="輸入排序位置"
                @click.stop
                @keydown.stop
                @keydown.enter.prevent="moveToOrder(idx, $event.target.value)"
                @blur="moveToOrder(idx, $event.target.value)"
              />
            </div>
            <button type="button" class="btn-icon btn-icon--danger" title="移除此商品" @click="removeHallProduct(idx)">
              <i class="ri-close-line"></i>
            </button>
          </div>
        </div>
        <button type="button" class="btn btn-secondary add-product-btn" @click="openProductPicker">
          <i class="ri-add-line"></i> 新增商品
        </button>
      </div>
    </div>

    <ProductPickerModal
      :open="showProductPicker"
      product-level-only
      title="挑選商品（館別以商品為單位，多規格請按一次「加入」）"
      @close="showProductPicker = false"
      @select="onProductSelected"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePageTitle } from '@/composables/usePageTitle.js'
import { commerceSettingsApi } from '@/api/modules/commerceSettings.js'
import { showError, showSuccess } from '@/utils/swal.js'
import ProductPickerModal from '@/components/ProductPickerModal.vue'

const route = useRoute()
const router = useRouter()

const hallId = computed(() => route.params.hallId)

usePageTitle('館別商品')

const loadingMain = ref(false)
const loadError = ref('')
const saving = ref(false)
const hallName = ref('')
const hallProducts = ref([])
const showProductPicker = ref(false)
const dragIndex = ref(null)
const dragOverIndex = ref(null)

function goBack() {
  router.push('/client/commerce/halls')
}

async function loadAll() {
  const id = hallId.value
  if (!id) {
    loadError.value = '缺少館別 ID'
    return
  }
  loadingMain.value = true
  loadError.value = ''
  try {
    const [hallRes, prodRes] = await Promise.all([
      commerceSettingsApi.getHall(id),
      commerceSettingsApi.getHallProducts(id),
    ])
    const hall = hallRes?.data ?? {}
    hallName.value = hall.name ?? ''
    hallProducts.value = prodRes?.data?.items ?? []
  } catch (e) {
    loadError.value = e?.response?.data?.message || '載入失敗'
    hallName.value = ''
    hallProducts.value = []
  } finally {
    loadingMain.value = false
  }
}

function openProductPicker() {
  showProductPicker.value = true
}

/** 與後端／商品列表一致：主檔或相簿圖 URL（API 可能為 snake 或 camel） */
function hallProductImageUrl(p) {
  if (!p || typeof p !== 'object') return ''
  const u =
    p.main_image_url ||
    p.mainImageUrl ||
    p.imageUrl ||
    p.image_url ||
    ''
  return typeof u === 'string' ? u.trim() : ''
}

function onProductSelected({ product }) {
  if (!product) return
  const exists = hallProducts.value.some((row) => row.product_id === product.id)
  if (!exists) {
    const img = product.imageUrl || product.mainImageUrl || product.main_image_url || product.image_url || ''
    hallProducts.value.push({
      product_id: product.id,
      product_name: product.name,
      main_image_url: typeof img === 'string' ? img.trim() : '',
    })
  }
  showProductPicker.value = false
}

function removeHallProduct(idx) {
  hallProducts.value.splice(idx, 1)
}

function moveProduct(from, to) {
  if (from === to) return
  if (from < 0 || from >= hallProducts.value.length) return
  if (to < 0 || to >= hallProducts.value.length) return
  const moved = hallProducts.value[from]
  if (!moved) return
  hallProducts.value.splice(from, 1)
  hallProducts.value.splice(to, 0, moved)
}

function moveUp(idx) {
  moveProduct(idx, idx - 1)
}

function moveDown(idx) {
  moveProduct(idx, idx + 1)
}

function moveToOrder(from, rawValue) {
  const length = hallProducts.value.length
  if (!length) return
  const parsed = Number.parseInt(String(rawValue ?? ''), 10)
  if (Number.isNaN(parsed)) return
  const target = Math.max(1, Math.min(parsed, length)) - 1
  moveProduct(from, target)
}

function onDragStart(idx, event) {
  dragIndex.value = idx
  dragOverIndex.value = idx
  if (event?.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(idx))
  }
}

function onDragOver(idx) {
  if (dragIndex.value === null || dragIndex.value === idx) return
  dragOverIndex.value = idx
}

function onDragLeave(idx) {
  if (dragOverIndex.value === idx) {
    dragOverIndex.value = null
  }
}

function onDrop(idx) {
  const from = dragIndex.value
  if (from === null || from === idx) {
    onDragEnd()
    return
  }
  moveProduct(from, idx)
  onDragEnd()
}

function onDragEnd() {
  dragIndex.value = null
  dragOverIndex.value = null
}

async function saveHallProducts() {
  const id = hallId.value
  if (!id) return
  saving.value = true
  try {
    await commerceSettingsApi.updateHallProducts(
      id,
      hallProducts.value.map((p) => ({ productId: p.product_id }))
    )
    await showSuccess('商品清單已儲存')
  } catch (e) {
    await showError(e?.response?.data?.message || '儲存失敗')
  } finally {
    saving.value = false
  }
}

onMounted(loadAll)
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  margin-bottom: 0;
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left--inline {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.header-actions { display: flex; align-items: center; gap: 8px; }

.page-header .page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #fff; color: #374151; border: 1px solid #d1d5db; }
.btn-secondary:hover:not(:disabled) { background: #f9fafb; }
.btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

.spinner-sm {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: hall-products-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes hall-products-spin {
  to { transform: rotate(360deg); }
}

.loading-state,
.error-state {
  background: #fff;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  color: #6b7280;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}
.error-state { color: #b91c1c; }
.error-state .btn { margin-left: 10px; }

.form-container {
  max-width: 720px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.intro-hint { margin: 0 0 16px; font-size: 13px; color: #6b7280; }

.empty-hint { color: #9ca3af; font-size: 13px; padding: 12px 0 16px; }

.hall-products-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }

.hall-product-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  cursor: grab;
  transition: background 0.15s, border-color 0.15s, opacity 0.15s;
}

.hall-product-row:hover {
  background: #f3f4f6;
}

.hall-product-row.dragging {
  opacity: 0.45;
  background: #e0e7ff;
}

.hall-product-row.drag-over {
  border-color: #3b82f6;
  background: #eff6ff;
}

.drag-handle {
  color: #9ca3af;
  font-size: 18px;
  line-height: 1;
  cursor: grab;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.hall-product-thumb {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 4px;
}

.hall-product-thumb-placeholder {
  width: 44px;
  height: 44px;
  background: #f3f4f6;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #d1d5db;
}

.hall-product-name { flex: 1; font-size: 14px; color: #374151; }

.sort-controls {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.sort-input {
  width: 60px;
  height: 30px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 13px;
  color: #374151;
}

.add-product-btn { margin-top: 4px; }

.btn-icon {
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 14px;
}
.btn-icon--danger { color: #ef4444; border-color: #fecaca; }
.btn-icon--danger:hover { background: #fff1f2; }

@media (max-width: 768px) {
  .page-container { padding: 12px; padding-top: 72px; }
  .form-container { padding: 16px; }
}
</style>

<style>
.page-header-hall-products {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 100;
}
.sidebar.collapsed ~ .main-content .page-header-hall-products {
  left: 64px;
}
@media (max-width: 768px) {
  .page-header-hall-products {
    left: 0 !important;
    top: 56px;
  }
}
</style>
